import type { Bet, RoundSnapshot, GameSelection } from '@urbanrats/contracts';
import { fromUtcInstant } from '@urbanrats/domain';
import { useI18n, type MessageKey } from '../i18n/index.ts';
import { fmtAtomic } from '../lib/format.ts';
import type { RoundOutcome } from '../useGame.ts';

function phase(r: RoundSnapshot): { key: MessageKey; from: number; to: number } | null {
  const t = r.manifest.timing;
  switch (r.state) {
    case 'BETTING_OPEN':
      return { key: 'phase.closesIn', from: fromUtcInstant(t.bettingOpenAt), to: fromUtcInstant(t.bettingCloseAt) };
    case 'BETTING_CLOSED':
      return { key: 'phase.countStartsIn', from: fromUtcInstant(t.bettingCloseAt), to: fromUtcInstant(t.captureStartAt) };
    case 'CAPTURING':
      return { key: 'phase.countEndsIn', from: fromUtcInstant(t.captureStartAt), to: fromUtcInstant(t.captureEndAt) };
    case 'REVIEW_REQUIRED':
      return { key: 'phase.reviewDeadline', from: fromUtcInstant(t.captureEndAt), to: fromUtcInstant(t.verifyDeadlineAt) };
    case 'RESULT_FINAL':
      // Live-camera round: the locked footage plays from startAt to revealAt; the count runs with it.
      return r.playback ? { key: 'phase.countEndsIn', from: Date.parse(r.playback.startAt), to: Date.parse(r.playback.revealAt) } : null;
    default:
      return null;
  }
}

/** What the player is watching: while the locked footage of a live round plays, that is the count, not a final result. */
function shownState(r: RoundSnapshot, now: number, pictureMs: number | null = null): RoundSnapshot['state'] {
  // LIVE round: the chip follows the picture on screen (capture time), which runs a few seconds behind.
  if (r.manifest.sourceMode === 'LIVE' && pictureMs !== null && ['BETTING_CLOSED', 'CAPTURING', 'VERIFYING', 'RESULT_FINAL', 'SETTLING', 'SETTLED'].includes(r.state)) {
    if (pictureMs < Date.parse(r.manifest.timing.captureStartAt)) return 'BETTING_CLOSED';
    if (pictureMs < Date.parse(r.manifest.timing.captureEndAt)) return 'CAPTURING';
    return r.state === 'BETTING_CLOSED' || r.state === 'CAPTURING' ? 'VERIFYING' : r.state;
  }
  if (r.state !== 'RESULT_FINAL' || !r.playback) return r.state;
  return now < Date.parse(r.playback.startAt) ? 'VERIFYING' : now < Date.parse(r.playback.revealAt) ? 'CAPTURING' : r.state;
}

interface Props {
  round: RoundSnapshot;
  now: number;
  outcome: RoundOutcome | null;
  roundBets: Bet[];
  pulse: number;
  /** LIVE rounds: capture time of the picture on screen. */
  pictureMs?: number | null;
  /** LIVE: the new round's band numbers are not shown yet (they arrive with the globe intro). */
  concealBands?: boolean;
}

export function CountPanel({ round, now, outcome, roundBets, pulse, pictureMs = null, concealBands = false }: Props) {
  const { t, intl } = useI18n();
  const state = shownState(round, now, pictureMs);
  const tm = round.manifest.timing;
  const onPicture = round.manifest.sourceMode === 'LIVE' && pictureMs !== null && round.state !== 'BETTING_OPEN';
  // LIVE: countdowns of the counting phase run on the picture's own clock.
  const p = onPicture
    ? state === 'BETTING_CLOSED'
      ? { key: 'phase.countStartsIn' as MessageKey, from: fromUtcInstant(tm.bettingCloseAt), to: fromUtcInstant(tm.captureStartAt) }
      : state === 'CAPTURING'
        ? { key: 'phase.countEndsIn' as MessageKey, from: fromUtcInstant(tm.captureStartAt), to: fromUtcInstant(tm.captureEndAt) }
        : null
    : phase(round);
  const clock = onPicture ? pictureMs : now;
  const remaining = p ? Math.max(0, Math.ceil((p.to - clock) / 1000)) : 0;
  const progress = p ? Math.min(1, Math.max(0, (clock - p.from) / (p.to - p.from))) : 1;
  const rules = round.manifest.rules;
  const count = round.finalCount ?? round.count;
  const m = round.metrics;
  const main =
    rules.kind === 'DUEL' ? `${m.countA ?? 0}–${m.countB ?? 0}` : rules.kind === 'FIRST_EVENT' ? (m.firstClass ?? '—') : String(count);
  const label =
    rules.kind === 'DUEL' ? 'A–B' : rules.kind === 'FIRST_EVENT' ? t('first.is', { cls: '' }).replace(':', '').trim() : round.manifest.includedClasses.includes('boat') ? t('count.boats') : round.manifest.includedClasses.includes('bicycle') ? t('count.bicycles') : `${t('count.label')} C`;

  return (
    <section className="count-panel" aria-label={t('count.label')} data-mechanic={rules.kind}>
      {/* Remounting on each counted crossing replays the 250 ms highlight animation. */}
      <div key={pulse} className={`count-box ${pulse > 0 ? 'lit' : ''}`} data-testid="count">
        <span className="count-label">{label}</span>
        <span className="count-value" aria-live="off" data-testid="count-value">
          {main}
        </span>
      </div>
      <div className="phase-box">
        <div className="phase-row">
          <span className={`state-chip state-${state}`} role="status" aria-live="polite">
            {t(`state.${state}` as MessageKey)}
          </span>
          {p && (onPicture || round.state !== 'RESULT_FINAL' || state === 'CAPTURING') && (
            <span className="phase-time">
              {t(p.key)} <strong className="tabular">{t('phase.seconds', { n: remaining })}</strong>
            </span>
          )}
        </div>
        <div className="progress" aria-hidden>
          <div className="progress-fill" style={{ transform: `scaleX(${progress})` }} />
        </div>
        <MechanicScale round={round} count={count} conceal={concealBands} />
      </div>
      <Outcome round={round} outcome={outcome} roundBets={roundBets} intl={intl} />
    </section>
  );
}

/** Where the live figure stands relative to the selections (text + position, never colour alone). */
function MechanicScale({ round, count, conceal = false }: { round: RoundSnapshot; count: number; conceal?: boolean }) {
  const { t } = useI18n();
  const rules = round.manifest.rules;
  const m = round.metrics;
  if (rules.kind === 'COUNT_BANDS') {
    const { low, high } = rules.thresholds;
    // Fixed visual widths per band (the single-count bands stay readable when the thresholds are large,
    // e.g. a busy camera's 35/41); inside a band the marker moves linearly with the count.
    const overSpan = Math.max(4, Math.ceil((high - low) * 0.75), count - high);
    const stops: [number, number][] = [
      [0, 0],
      [low, 38],
      [low + 1, 47],
      [high, 71],
      [high + 1, 80],
      [high + 1 + overSpan, 100],
    ];
    const pos = (v: number) => {
      for (let i = 1; i < stops.length; i++) {
        const [v0, p0] = stops[i - 1]!;
        const [v1, p1] = stops[i]!;
        if (v <= v1) return p0 + ((v - v0) / (v1 - v0)) * (p1 - p0);
      }
      return 100;
    };
    const band = (from: number, to: number) => ({ left: `${pos(from)}%`, width: `${pos(to) - pos(from)}%` });
    return (
      <div className="scale" aria-label={t('count.bands', { low, high })}>
        <div className="band band-under" style={band(0, low)}>
          <span>{conceal ? '–' : `≤ ${low - 1}`}</span>
        </div>
        <div className="band band-exact" style={band(low, low + 1)}>
          <span>{conceal ? '–' : low}</span>
        </div>
        <div className="band band-range" style={band(low + 1, high)}>
          <span>{conceal ? '–' : `${low + 1}–${high - 1}`}</span>
        </div>
        <div className="band band-exact" style={band(high, high + 1)}>
          <span>{conceal ? '–' : high}</span>
        </div>
        <div className="band band-over" style={band(high + 1, high + 1 + overSpan)}>
          <span>{conceal ? '–' : `≥ ${high + 1}`}</span>
        </div>
        <div className="scale-marker" style={{ left: `${pos(Math.min(count, high + overSpan) + 0.5)}%` }} aria-hidden>
          ▼
        </div>
      </div>
    );
  }
  if (rules.kind === 'COUNT_BUCKETS') {
    return (
      <div className="buckets" role="list">
        {rules.buckets.map((b) => {
          const on = count >= b.min && (b.max === null || count <= b.max);
          return (
            <span key={b.selection} role="listitem" className={`bucket ${on ? 'on' : ''}`} aria-current={on || undefined}>
              {on ? '▶ ' : ''}
              {t(`sel.${b.selection}` as MessageKey)}
            </span>
          );
        })}
      </div>
    );
  }
  if (rules.kind === 'DUEL') {
    const a = m.countA ?? 0;
    const b = m.countB ?? 0;
    const total = Math.max(1, a + b);
    return (
      <div className="duel" aria-label={`A ${a}, B ${b}`}>
        <span className="duel-side">
          {t('duel.a')} <strong className="tabular">{a}</strong>
        </span>
        <div className="duel-bar" aria-hidden>
          <div className="duel-a" style={{ width: `${(a / total) * 100}%` }} />
        </div>
        <span className="duel-side">
          <strong className="tabular">{b}</strong> {t('duel.b')}
        </span>
      </div>
    );
  }
  return (
    <p className="first-event" role="status">
      {m.firstClass ? t('first.is', { cls: m.firstClass }) : round.state === 'CAPTURING' || round.state === 'BETTING_OPEN' || round.state === 'BETTING_CLOSED' ? t('first.waiting') : t('first.none')}
    </p>
  );
}

function Outcome({ round, outcome, roundBets, intl }: { round: RoundSnapshot; outcome: RoundOutcome | null; roundBets: Bet[]; intl: string }) {
  const { t } = useI18n();
  if (round.state === 'REVIEW_REQUIRED')
    return (
      <p className="outcome warn" role="status">
        {t('result.review')}
      </p>
    );
  if (round.state === 'VOIDING' || round.state === 'VOIDED' || outcome?.kind === 'VOIDED')
    return (
      <p className="outcome warn" role="status">
        {t('result.voided')}
      </p>
    );
  if (!outcome || outcome.finalCount === null) return null;
  const winners = outcome.winning.map((w) => t(`sel.${w as GameSelection}` as MessageKey)).join(', ');
  const mine = roundBets.filter((b) => b.payoutGross !== null);
  let mineText: string | null = null;
  if (mine.length > 0) {
    const cur = mine[0]!.stake;
    const stake = mine.reduce((a, b) => a + BigInt(b.stake.amountAtomic), 0n);
    const ret = mine.reduce((a, b) => a + BigInt(b.payoutGross!.amountAtomic), 0n);
    const amount = fmtAtomic(ret, cur.scale, cur.currency, intl);
    // No celebration when the return is below the stake.
    const pending = mine.some((b) => b.state !== 'SETTLED');
    // Never say "returned/paid" while the credit is still pending; no celebration when return < stake.
    mineText =
      ret === 0n ? t('result.noWin') : pending ? t('result.creditPending', { amount }) : ret < stake ? t('result.youReturnedLess', { amount }) : t('result.youWon', { amount });
  }
  return (
    <div className="outcome" role="status" data-testid="outcome">
      <strong>{t('result.final', { count: outcome.finalCount })}</strong>
      <span>{t('result.winners', { list: winners })}</span>
      {mineText && <span className="outcome-mine">{mineText}</span>}
    </div>
  );
}
